import { useEffect, useState } from 'react'
import { STAGE_FLOW, STAGE_META } from '@reptree/shared'
import { TreeMark } from '../Logo'

/** Browser/app chrome frame so each animated mock reads as a real product screen. */
export function MockFrame({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="overflow-hidden rounded-card border bg-[color:rgb(var(--bg-elev))] hairline">
      <div className="flex items-center gap-2 border-b px-4 py-2.5 hairline">
        <span className="h-2.5 w-2.5 rounded-full bg-[color:rgb(var(--brand-primary)/0.2)]" />
        <span className="h-2.5 w-2.5 rounded-full bg-[color:rgb(var(--brand-accent)/0.35)]" />
        <span className="h-2.5 w-2.5 rounded-full bg-[color:rgb(var(--tint-3))]" />
        <span className="label-mono ml-2 text-[color:rgb(var(--muted))]">{label}</span>
      </div>
      <div className="p-4">{children}</div>
    </div>
  )
}

const SOURCES = ['Casting Networks', 'Actors Access', 'Spotlight', 'Email'] as const
const INBOX = [
  { from: 'Casting Networks', role: 'Series Regular', show: 'Untitled HBO Pilot' },
  { from: 'Email · WeTransfer', role: 'Co-Star', show: 'Abbott Elementary' },
  { from: 'Actors Access', role: 'Guest Star', show: 'Law & Order' },
  { from: 'Spotlight', role: 'Supporting', show: 'Indie Feature — "Driftwood"' },
]

/** Unified inbox: requests from every rail land and normalize into one tracked queue. */
export function InboxMock() {
  const [n, setN] = useState(1)
  useEffect(() => {
    const t = setInterval(() => setN((v) => (v >= INBOX.length ? 1 : v + 1)), 1400)
    return () => clearInterval(t)
  }, [])
  return (
    <MockFrame label="Casting inbox">
      <div className="mb-3 flex flex-wrap gap-1.5">
        {SOURCES.map((s) => (
          <span key={s} className="rounded-full bg-[color:rgb(var(--tint-1))] px-2.5 py-1 text-xs text-[color:rgb(var(--muted))]">
            {s}
          </span>
        ))}
      </div>
      {/* All rows are ALWAYS in the layout (constant height — nothing below ever reflows);
          arrival animates purely with opacity/transform via the reveal classes. */}
      <div className="space-y-2">
        {INBOX.map((it, i) => (
          <div
            key={it.show}
            className={`reveal flex items-center justify-between rounded-nav bg-[color:rgb(var(--tint-1))] px-3 py-2.5 ${i < n ? 'is-in' : ''}`}
            style={{ transitionDelay: `${i * 60}ms` }}
          >
            <div className="min-w-0">
              <div className="truncate font-display">{it.show}</div>
              <div className="label-mono text-[color:rgb(var(--muted))]">{it.from}</div>
            </div>
            <span className="pill shrink-0">{it.role}</span>
          </div>
        ))}
      </div>
    </MockFrame>
  )
}

/** Self-tape vault: branded upload, version stack, playback, release-to-casting. */
export function TapeVaultMock() {
  const [pct, setPct] = useState(18)
  useEffect(() => {
    const t = setInterval(() => setPct((v) => (v >= 100 ? 12 : v + 4)), 240)
    return () => clearInterval(t)
  }, [])
  return (
    <MockFrame label="Self-tape vault · Isa Steiner">
      <div className="relative aspect-video overflow-hidden rounded-nav bg-gradient-to-br from-forest to-moss">
        <button className="absolute inset-0 grid place-items-center" aria-hidden>
          <span className="grid h-12 w-12 place-items-center rounded-full bg-[#fffefd]/90 text-forest">
            <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
          </span>
        </button>
        <div className="absolute bottom-0 left-0 right-0 p-2.5">
          <div className="h-1 overflow-hidden rounded-full bg-white/25">
            <div className="h-full rounded-full bg-[color:rgb(var(--brand-accent))]" style={{ width: `${pct}%` }} />
          </div>
        </div>
        <span className="absolute right-2 top-2 rounded-full bg-black/40 px-2 py-0.5 text-xs text-white">v3 · current</span>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <span className="pill">Slate ✓</span>
        <span className="rounded-full bg-[color:rgb(var(--tint-1))] px-2.5 py-1 text-xs text-[color:rgb(var(--muted))]">Request retake</span>
        <span className="rounded-full bg-[color:rgb(var(--tint-1))] px-2.5 py-1 text-xs text-[color:rgb(var(--muted))]">Reorder</span>
        <span className="rounded-full bg-[color:rgb(var(--tint-1))] px-2.5 py-1 text-xs text-[color:rgb(var(--muted))]">Move to Drive</span>
        <span className="btn-brass !px-3 !py-1.5 text-xs">Release to casting<span className="cta-arrow !h-4 !w-4">→</span></span>
      </div>
    </MockFrame>
  )
}

/** Live journey: a role advancing audition → booked, with a moving fill. */
export function JourneyMock() {
  const [idx, setIdx] = useState(1)
  useEffect(() => {
    const t = setInterval(() => setIdx((v) => (v >= STAGE_FLOW.length - 1 ? 0 : v + 1)), 1500)
    return () => clearInterval(t)
  }, [])
  return (
    <MockFrame label="Abbott Elementary · Kid 2">
      <div className="space-y-2.5">
        {STAGE_FLOW.map((s, i) => {
          const on = i <= idx
          return (
            <div key={s} className="flex items-center gap-3">
              <span
                className={`grid h-7 w-7 place-items-center rounded-full text-xs transition ${
                  on ? 'bg-[color:rgb(var(--brand-primary))] text-[#fffefd]' : 'bg-[color:rgb(var(--tint-1))] text-[color:rgb(var(--muted))]'
                }`}
              >
                {i + 1}
              </span>
              <div className="h-2 flex-1 overflow-hidden rounded-full bg-[color:rgb(var(--tint-1))]">
                <div
                  className="h-full rounded-full bg-[color:rgb(var(--brand-accent))] transition-all duration-700"
                  style={{ width: on ? '100%' : '0%' }}
                />
              </div>
              <span className={`label-mono w-20 text-right ${on ? '' : 'text-[color:rgb(var(--muted))]'}`}>{STAGE_META[s].label}</span>
            </div>
          )
        })}
      </div>
    </MockFrame>
  )
}

/** Client portal — phone-framed live status the actor sees. */
export function PortalMock() {
  const [stage, setStage] = useState(0)
  useEffect(() => {
    const t = setInterval(() => setStage((v) => (v >= 2 ? 0 : v + 1)), 1800)
    return () => clearInterval(t)
  }, [])
  const stages = ['audition', 'callback', 'offer'] as const
  return (
    <div className="mx-auto w-[260px] rounded-[2.2rem] border-[6px] border-[color:rgb(var(--fg))] bg-[color:rgb(var(--bg-elev))] p-3 shadow-none">
      <div className="mx-auto mb-3 h-1.5 w-16 rounded-full bg-[color:rgb(var(--hairline)/0.2)]" />
      <div className="flex items-center gap-2">
        <TreeMark className="h-5 w-5" />
        <span className="font-display">Your auditions</span>
      </div>
      <div className="mt-3 space-y-2">
        <div className="rounded-nav bg-tint2 p-3">
          <div className="font-display">Abbott Elementary</div>
          <div className="label-mono text-[color:rgb(var(--muted))]">Co-Star</div>
          <span className="pill mt-2">{STAGE_META[stages[stage]!].label}</span>
        </div>
        <div className="rounded-nav bg-tint1 p-3">
          <div className="font-display">Driftwood</div>
          <div className="label-mono text-[color:rgb(var(--muted))]">Supporting · self-tape due Fri</div>
          <div className="btn-brass mt-2 !px-3 !py-1.5 text-xs">Upload tape<span className="cta-arrow !h-4 !w-4">→</span></div>
        </div>
      </div>
    </div>
  )
}
